import { Alert } from '@mui/material';
import { DateTime } from 'luxon';
import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MAX_NON_CASH_MONTHLY_BUDGET_CENTS } from '../../../constants';
import { useBenefitActivations } from '../ActivationsList/useBenefitActivations';

interface Props {
  employeeId?: string;
  benefit: BenefitDTOBenefitEnum;
  customBenefitId?: string;
  monthlyBudget?: number;
}

export const NonCashLimitWarning: React.FC<Props> = ({ employeeId, benefit, customBenefitId, monthlyBudget }) => {
  const { t } = useTranslation('benefitsModule');
  const { watch, setError, clearErrors, formState } = useFormContext();

  const formStartDate: Date = watch('startDate') || formState.defaultValues?.startDate;
  const startDate = DateTime.fromJSDate(formStartDate) < DateTime.now() ? DateTime.now().startOf('month').toJSDate() : formStartDate;
  const endDate: Date = watch('endDate');

  const { data: activations } = useBenefitActivations(employeeId, {
    startDate,
    endDate,
    excludeBenefit: benefit,
    excludeCustomBenefitId: customBenefitId,
  });

  const usedNonCashSum = activations?.nonCashBenefitSum || 0;
  const amount = typeof monthlyBudget === 'number' ? monthlyBudget : ((monthlyBudget && parseFloat(monthlyBudget)) ?? 0);
  const exceededNonCashLimitAmount = usedNonCashSum + (amount || 0) - MAX_NON_CASH_MONTHLY_BUDGET_CENTS;
  const hasExceededNonCashLimit = exceededNonCashLimitAmount > 0;

  useEffect(() => {
    if (hasExceededNonCashLimit) {
      setError('root', { message: 'error' });
      return () => {
        clearErrors('root');
      };
    }
  }, [clearErrors, hasExceededNonCashLimit, setError]);

  return (
    <>
      {hasExceededNonCashLimit && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('nonCashLimitWarning', { exceededNonCashLimitAmount })}
        </Alert>
      )}
      {!employeeId && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('nonCashLimitWarningGeneric', { benefit })}
        </Alert>
      )}
    </>
  );
};
